import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';
import { TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/solid';

export default function MyModuleLessons() {
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchLessons = async () => {
      const { data, error } = await supabase
        .from('module_lessons')
        .select('id, title, module_id, created_at');

      if (error) {
        console.error('Failed to fetch lessons:', error.message);
        setError('Failed to load module lessons.');
      } else {
        setLessons(data);
      }
    };

    fetchLessons();
  }, [user, navigate]);

  const handleDeleteLesson = async (lessonId) => {
    const confirm = window.confirm('Are you sure you want to delete this module lesson?');
    if (!confirm) return;

    const { error } = await supabase
      .from('module_lessons')
      .delete()
      .eq('id', lessonId);

    if (error) {
      alert('Failed to delete module lesson: ' + error.message);
    } else {
      setLessons((prev) => prev.filter((l) => l.id !== lessonId));
    }
  };

  const filtered = lessons.filter((lesson) =>
    lesson.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-800">My Module Lessons</h2>
        {user?.isAdmin && (
          <div className="flex gap-2">
            <Link to="/admin/create-module-lesson">
              <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500">
                <PlusIcon className="w-5 h-5" />
                Create Module Lesson
              </button>
            </Link>
            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600"
            >
              <PencilIcon className="w-5 h-5" />
              {editMode ? 'Done Editing' : 'Edit'}
            </button>
          </div>
        )}
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search module lessons..."
        className="w-full p-3 border border-gray-300 rounded"
      />

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 mt-4">
          {filtered.map((lesson) => (
            <div
              key={lesson.id}
              className="p-4 bg-white border border-gray-200 rounded flex justify-between items-center"
            >
              <Link
                to={`/modules/view/${lesson.module_id}/lesson/${lesson.id}`}
                className="text-blue-700 font-medium hover:underline"
              >
                {lesson.title}
              </Link>
              {editMode && user?.isAdmin && (
                <button onClick={() => handleDeleteLesson(lesson.id)}>
                  <TrashIcon className="w-5 h-5 text-gray-400 hover:text-red-500" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 mt-4">No module lessons found.</p>
      )}

      {error && <p className="text-red-500">{error}</p>}
    </div>
  );
}
